import { useParams, Link } from 'react-router-dom';
import { useAppointmentStatus, useCancelAppointment } from '../hooks/useApi';
import { ChevronRight, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';

const statusConfig: Record<string, { icon: typeof Clock; color: string; bg: string; label: string }> = {
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Pending Review' },
  confirmed: { icon: CheckCircle, color: 'text-primary', bg: 'bg-primary-50', label: 'Confirmed' },
  cancelled: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Cancelled' },
  completed: { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Completed' },
  no_show: { icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-100', label: 'No Show' },
};

export default function AppointmentStatusPage() {
  const { token } = useParams<{ token: string }>();
  const { data: appointment, isLoading, error } = useAppointmentStatus(token || '');
  const cancelMutation = useCancelAppointment();

  const handleCancel = () => {
    if (!token) return;
    if (confirm('Are you sure you want to cancel this appointment?')) {
      cancelMutation.mutate(token);
    }
  };

  if (isLoading) {
    return (
      <div role="status" aria-busy="true" aria-label="Loading appointment status" className="max-w-2xl mx-auto px-4 py-8">
        <div className="skeleton h-4 w-48 mb-6" />
        <div className="card">
          <div className="skeleton h-8 w-32 rounded-full mb-6" />
          <div className="skeleton h-7 w-56 mb-6" />
          <div className="grid grid-cols-2 gap-4">
            <div className="skeleton h-16 rounded-lg" />
            <div className="skeleton h-16 rounded-lg" />
            <div className="skeleton h-16 rounded-lg" />
            <div className="skeleton h-16 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-navy mb-4">Appointment Not Found</h2>
        <p className="text-gray-400 mb-6">Please check your verification link.</p>
        <Link to="/" className="btn btn-primary">Go Home</Link>
      </div>
    );
  }

  const config = (statusConfig[appointment.status] ?? statusConfig.pending)!;
  const StatusIcon = config.icon;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-navy font-medium">Appointment Status</span>
      </div>

      <div className="card">
        {/* Status badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${config.bg} ${config.color} font-semibold text-sm mb-6`}>
          <StatusIcon className="w-4 h-4" />
          {config.label}
        </div>

        <h1 className="text-2xl font-extrabold text-navy mb-6">Appointment Details</h1>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-cream rounded-lg p-3">
            <p className="label mb-0.5">Clinic</p>
            <p className="text-navy font-medium text-sm">{appointment.clinic_name}</p>
          </div>
          {appointment.service_name && (
            <div className="bg-cream rounded-lg p-3">
              <p className="label mb-0.5">Service</p>
              <p className="text-navy font-medium text-sm">{appointment.service_name}</p>
            </div>
          )}
          <div className="bg-cream rounded-lg p-3">
            <p className="label mb-0.5">Name</p>
            <p className="text-navy font-medium text-sm">{appointment.contact_name}</p>
          </div>
          <div className="bg-cream rounded-lg p-3">
            <p className="label mb-0.5">Phone</p>
            <p className="text-navy font-medium text-sm">{appointment.contact_phone}</p>
          </div>
          {appointment.preferred_date && (
            <div className="bg-cream rounded-lg p-3">
              <p className="label mb-0.5">Preferred Date</p>
              <p className="text-navy font-medium text-sm">{appointment.preferred_date}</p>
            </div>
          )}
          <div className="bg-cream rounded-lg p-3">
            <p className="label mb-0.5">Time Slot</p>
            <p className="text-navy font-medium text-sm capitalize">{appointment.preferred_time_slot}</p>
          </div>
          {appointment.confirmed_datetime && (
            <div className="bg-primary-50 rounded-lg p-3 col-span-2">
              <p className="label mb-0.5">Confirmed Date & Time</p>
              <p className="text-primary font-semibold text-sm">
                {new Date(appointment.confirmed_datetime).toLocaleString()}
              </p>
            </div>
          )}
          <div className="bg-cream rounded-lg p-3">
            <p className="label mb-0.5">Submitted</p>
            <p className="text-navy font-medium text-sm">
              {new Date(appointment.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Actions */}
        {appointment.status === 'pending' && (
          <button onClick={handleCancel} disabled={cancelMutation.isPending} className="btn btn-danger mt-6">
            {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Appointment'}
          </button>
        )}

        {/* Save link */}
        <div className="mt-6 bg-cream rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Save this link to check your status later:</p>
          <code className="text-xs text-primary break-all">{window.location.href}</code>
        </div>
      </div>
    </div>
  );
}
